import { GetProjectEstimatesQuery } from '@/gql/graphql';
import {
  getClientPortalGateReason,
  useHasActiveSubdomain,
} from '@/ui/components/estimatesAndProposals/draftSendEmail/clientPortalGate';
import { PanelHeaderBar } from '@/ui/components/estimatesAndProposals/draftSendEmail/panelLayout';
import { Button, Tooltip } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import ConvertToProposalButton from './ConvertToProposalButton';
import EstimateHeaderIdentity from './EstimateHeaderIdentity';

type Estimate = GetProjectEstimatesQuery['projectById']['estimates'][number];

const getSendDisabledReason = (params: {
  hasActiveSubdomain: boolean;
  hasPricedLineItem: boolean;
}): string | null => {
  const { hasActiveSubdomain, hasPricedLineItem } = params;
  if (!hasActiveSubdomain) {
    return getClientPortalGateReason('estimate');
  }
  if (!hasPricedLineItem) {
    return 'Add at least one line item with a price before sending.';
  }
  return null;
};

type EstimateBuilderSidebarHeaderProps = {
  projectId: string;
  estimateId: string;
  estimate: Estimate | undefined;
  onEnterSendMode: () => void;
  onResend: () => void;
};

const EstimateBuilderSidebarHeader: FC<EstimateBuilderSidebarHeaderProps> = (
  props
) => {
  const { projectId, estimateId, estimate, onEnterSendMode, onResend } = props;

  const hasActiveSubdomain = useHasActiveSubdomain();

  const isSent = estimate?.status === 'SENT';
  const isArchived = estimate?.status === 'ARCHIVED';
  const hasPricedLineItem =
    estimate?.sections.some((section) =>
      section.lineItems.some((item) => item.unitPriceExcTax.value > 0)
    ) ?? false;
  const sendDisabledReason = getSendDisabledReason({
    hasActiveSubdomain,
    hasPricedLineItem,
  });
  const sendDisabled = sendDisabledReason !== null;
  const showPreviewButton = estimate?.status === 'DRAFT';

  return (
    <PanelHeaderBar>
      <EstimateHeaderIdentity
        projectId={projectId}
        estimateId={estimateId}
        showConvertInMenu={!isSent}
        onResend={onResend}
      />

      {!isArchived && showPreviewButton && (
        <Tooltip
          tooltipContent={sendDisabledReason ?? ''}
          enabled={sendDisabled}
        >
          {/*
            Gate with `aria-disabled` rather than the native `disabled`
            attribute so the button stays focusable: keyboard users can reach
            it and the tooltip reason opens on focus.
          */}
          <Button
            size="sm"
            aria-disabled={sendDisabled}
            onClick={() => {
              if (!sendDisabled) onEnterSendMode();
            }}
          >
            <UntitledIcon name="send-01.3" className="size-4" />
            Draft & send
          </Button>
        </Tooltip>
      )}

      {!isArchived && isSent && (
        <ConvertToProposalButton
          projectId={projectId}
          estimateId={estimateId}
        />
      )}
    </PanelHeaderBar>
  );
};

export default EstimateBuilderSidebarHeader;
